import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_HOST } from "../constants";
import { setTelegramUserInStorage } from "../utils/authStorage";
import type { TelegramUser } from "../types";

export const YandexAuthCallback = ({ onLogin }: { onLogin: (user: TelegramUser) => void }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<string>("Авторизация через Яндекс...");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");

    if (!code || !state) {
      setError("Отсутствуют параметры авторизации. Попробуйте снова.");
      setTimeout(() => navigate("/login", { replace: true }), 3000);
      return;
    }

    (async () => {
      try {
        setStatus("Обмен кода на токен...");
        const response = await fetch(`${API_HOST}/auth/yandex/callback`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, state }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || "Ошибка авторизации");
        }

        const data = await response.json();

        if (!data.success || !data.user_id) {
          throw new Error(data.message || "Авторизация не удалась");
        }

        setStatus("Загрузка данных пользователя...");

        // Fetch full user data
        const userResponse = await fetch(`${API_HOST}/user/${data.user_id}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!userResponse.ok) {
          throw new Error("Ошибка получения данных пользователя");
        }

        const userData = await userResponse.json();

        const user: TelegramUser = {
          id: userData.id,
          username: userData.username,
          first_name: userData.first_name,
          last_name: userData.last_name,
          photo_url: userData.photo_url,
          subscription_status: userData.subscription_status,
          progress: userData.progress || {},
        };

        setTelegramUserInStorage(JSON.stringify(user));
        onLogin(user);
        setStatus("Авторизация успешна! Перенаправление...");
        window.location.replace("/profile");
      } catch (err: any) {
        console.error("[YandexAuth] Error:", err);
        setError(err.message || "Произошла ошибка при авторизации");
        setTimeout(() => navigate("/login", { replace: true }), 3000);
      }
    })();
  }, [searchParams, navigate, onLogin]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-neutral-light">
      <div className="bg-white rounded-lg shadow p-6 text-center max-w-md w-full mx-4">
        {!error ? (
          <>
            <div className="mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
            </div>
            <p className="text-neutral-text font-body">{status}</p>
          </>
        ) : (
          <>
            <div className="mb-4 text-red-500">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-neutral-text font-body text-red-600">{error}</p>
            <p className="text-neutral-text/70 text-sm font-body mt-2">
              Перенаправление на страницу входа...
            </p>
          </>
        )}
      </div>
    </div>
  );
};
